import { useState } from "react";

import type { FlowSettingView } from "@/api/flowSettings";
import {
  classifyFlowWriteFailure,
  useFlowSettingsQuery,
  useReplaceFlowSettingsMutation,
  type FlowSettingWrite,
  type ProjectFlowSettings,
} from "@/api/flowSettings";
import { queryKeys } from "@/api/hooks";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import {
  FLOW_STATE_LABEL,
  canClearDefaultLanguage,
  languageChoices,
  languageName,
  languageWrites,
  settingOf,
} from "@/lib/flowSettings";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { Notice } from "@/ui/Notice";

/**
 * Conversation language — the named Project Rules control #855 adds (#854's
 * `supportedLanguages` / `defaultLanguage`).
 *
 * ## WHERE IT SITS, AND WHY IT IS ONE ROW
 *
 * `ProjectSectionPane`'s `controls` slot on the Rules section, in the shape
 * #860's Response filtering already established there: one compact bordered
 * row, name on the left, state on the right, its editor behind an action. The
 * [Lovable Rules
 * screen](https://noproductcontent.lovable.app/projects/mod/config/rules) is
 * compact adopted language-rule rows under a top-right adoption action, and
 * this is an explicit ADDITION to it, so it takes that screen's own shape and
 * touches none of the rows.
 *
 * ## THE THREE SENTENCES THIS SURFACE MUST GET RIGHT
 *
 * 1. **It narrows, it does not extend.** Language support is a property of the
 *    pinned engine, and content alone cannot make a new language work — so the
 *    choices offered are exactly `baseSupported`, read from the service.
 *
 * 2. **Narrowing the SESSION does not waive the LIBRARY invariant.** A Rules
 *    asset carries every supported language and is published as a bilingual
 *    identity; running Hebrew-only sessions does not make the English content
 *    optional, and the English part may keep inheriting its unmodified base
 *    text. An author who reads this control as "English can now be deleted"
 *    would be led into a publish refusal, so the dialog says it where the
 *    decision is made.
 *
 * 3. **This is not the report language.** Preview and report presentation are
 *    decided by the TARGET that renders them, not by the interview's session
 *    languages. Conflating the two is the specific misreading a control called
 *    "language" invites.
 */
export function ConversationLanguageControl({
  projectId,
}: {
  projectId: string;
}) {
  const query = useFlowSettingsQuery(projectId);
  const write = useCapability("draft.write");
  // `cancelQueries` then `refetchQueries`, never a bare `refetch()`: the state
  // this recovery is most needed in is a PARKED read, which answers `refetch()`
  // with the parked retryer's own promise without sending anything.
  const retryRead = useRetryPausedQuery(queryKeys.projectFlow(projectId));
  const [open, setOpen] = useState(false);

  const state = query.data ?? null;
  // `PathwayFlow`'s rule, from `fetchStatus` rather than `status`: a re-read in
  // flight and one PARKED are both supersessions, and `status` reports each of
  // them as `success` over the retained data. `isFetching` sees the first only,
  // which left a parked refetch classified as current — indefinitely, since a
  // parked one does not resolve on its own.
  const readState: "current" | "refreshing" | "paused" | "behind" =
    query.fetchStatus === "fetching"
      ? "refreshing"
      : query.fetchStatus === "paused"
        ? "paused"
        : query.isSuccess
          ? "current"
          : "behind";
  const settled = readState === "current";

  if (state === null) {
    // Deliberately quiet, not a skeleton: this control sits ABOVE the section's
    // rows, and a loading block there pushes the clinical body down the page on
    // every navigation. A failed or parked read gets the same line AND a way
    // back — without one, a first read that did not succeed left this control
    // dead until the route was re-entered.
    const reading = query.isPending && query.fetchStatus === "fetching";
    return (
      <div
        className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border bg-card px-5 py-4 text-sm text-muted-foreground"
        data-slot="conversation-language"
      >
        <p className="min-w-0 flex-1" role={reading ? "status" : "alert"}>
          {reading
            ? "Reading this project's conversation languages…"
            : query.fetchStatus === "paused"
              ? "Reading this project's conversation languages is waiting for the connection. The rules below are unaffected."
              : "This project's conversation languages could not be read. The rules below are unaffected."}
        </p>
        {reading ? null : (
          <Button size="sm" variant="outline" onClick={retryRead}>
            Read it again
          </Button>
        )}
      </div>
    );
  }

  // BOTH SETTINGS DECIDE THE BADGE. The row owns `supported_languages` AND
  // `default_language`, and reading only the first labelled a Project that had
  // authored just its opening language "Inherited" — a claim the Configure
  // dialog immediately contradicts. The label is the state of whichever one the
  // Project actually decided; where they differ, the authored one is the fact
  // worth showing, because "inherited" is the claim that would be false.
  const views = [
    settingOf(state, "supportedLanguages"),
    settingOf(state, "defaultLanguage"),
  ].filter((view): view is FlowSettingView => view !== null);
  const decided = views.find((view) => view.state !== "inherited") ?? null;
  const authored = decided !== null;

  return (
    <div
      data-slot="conversation-language"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-border bg-card px-5 py-4"
    >
      <div className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-foreground">
            Conversation language
          </span>
          <Badge tone={authored ? "accent" : "outline"}>
            {views.length === 0
              ? "Unknown"
              : FLOW_STATE_LABEL[(decided ?? views[0]!).state]}
          </Badge>
        </span>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {describe(state)}
        </p>
        {readState === "refreshing" ? (
          <p
            role="status"
            aria-live="polite"
            className="mt-0.5 text-sm text-muted-foreground"
          >
            Re-reading this project&apos;s conversation languages. What is shown
            is the previous reading until that lands.
          </p>
        ) : readState === "paused" || readState === "behind" ? (
          <p role="alert" className="mt-0.5 text-sm text-destructive">
            {readState === "paused"
              ? "A re-read of this project's conversation languages is waiting for the connection, so what is shown is the previous reading. It cannot be changed until that completes."
              : "This could not be re-read, so it may be behind what the Editor Service now holds. It cannot be changed until it can be read again."}{" "}
            <Button
              size="sm"
              variant="outline"
              className="ml-1 align-baseline"
              onClick={retryRead}
            >
              Read it again
            </Button>
          </p>
        ) : null}
      </div>

      <div className="relative z-10 flex shrink-0 items-center gap-1">
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          {/* One control whatever the account may do: a reader opens the same
              dialog and reads the effective languages there. Naming it
              "Configure" for a reader would promise a write they do not hold —
              and so would naming it that over a reading that failed to refresh. */}
          {write.allowed && settled ? "Configure" : "View"}
        </Button>
      </div>

      {open ? (
        <ConversationLanguageDialog
          projectId={projectId}
          state={state}
          canWrite={write.allowed && settled}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

function describe(state: ProjectFlowSettings): string {
  const { supported, baseSupported } = state.languages;
  const fallback = state.languages.default ?? null;
  if (supported.length === 0) {
    return "The Editor Service reports no supported language for this project's sessions, which the pinned engine will not load.";
  }
  const names = supported.map((code) => languageName(code)).join(", ");
  const scope =
    supported.length < baseSupported.length
      ? `${names} only — narrowed from the ${baseSupported.length} this pack supports.`
      : `${names}.`;
  const opening =
    fallback === null
      ? "No default language is set."
      : `Sessions open in ${languageName(fallback)}.`;
  return `${scope} ${opening} Report and preview language is decided by the target that renders them.`;
}

function ConversationLanguageDialog({
  projectId,
  state,
  canWrite,
  onClose,
}: {
  projectId: string;
  state: ProjectFlowSettings;
  canWrite: boolean;
  onClose: () => void;
}) {
  const mutation = useReplaceFlowSettingsMutation(projectId);
  const choices = languageChoices(state);
  // THE REVISION THIS EDIT IS BOUND TO, captured when the dialog opened and
  // never re-read — `ResponseFilteringControl`'s rule, and the defect round 1
  // had here too: a stale refusal refetches the flow read, so a second Save
  // would have carried the same selection bound to the version that refusal
  // fetched, passing the compare-and-set and overwriting a change nobody in
  // this dialog had seen.
  // ADVANCED ON EVERY ACCEPTED WRITE. Captured once and never moved, a second
  // save in the same dialog session carried the version from BEFORE the first
  // one — a guaranteed 409, which then tripped the stale block below and wedged
  // the dialog until it was closed and reopened. The point of holding it is
  // that it is not re-read from a REFETCH; a version the server itself just
  // handed back for this dialog's own write is the opposite of that.
  const [editVersion, setEditVersion] = useState(() => state.draftVersion);
  const [staleBlocked, setStaleBlocked] = useState(false);
  // ENTERED VALUES SURVIVE EVERY REFUSAL. Seeded once from the served state and
  // never re-seeded: a stale save, an invalid selection or a cancelled
  // confirmation leaves the author looking at what they chose, which is the
  // ticket's own criterion.
  const [supported, setSupported] = useState<string[]>(() => [
    ...state.languages.supported,
  ]);
  const [fallback, setFallback] = useState<string | null>(
    () => state.languages.default ?? null,
  );
  const [confirmReset, setConfirmReset] = useState(false);

  const failure = mutation.isError
    ? classifyFlowWriteFailure(mutation.error)
    : null;
  const supportedView = settingOf(state, "supportedLanguages");
  const defaultView = settingOf(state, "defaultLanguage");
  const authored =
    (supportedView !== null && supportedView.state !== "inherited") ||
    (defaultView !== null && defaultView.state !== "inherited");

  const clearable = canClearDefaultLanguage(state);
  const writes = languageWrites(state, {
    supported,
    defaultLanguage: fallback,
  });
  // The one rule this surface checks before the round trip, because the remedy
  // is a checkbox in the same dialog: the loader refuses a default language the
  // supported list does not carry.
  const defaultUnsupported =
    fallback !== null && supported.length > 0 && !supported.includes(fallback);
  const empty = supported.length === 0;

  function submit(settings: FlowSettingWrite[]) {
    if (staleBlocked) {
      return;
    }
    mutation.mutate(
      { expectedDraftVersion: editVersion, settings },
      {
        onSuccess(saved) {
          // The SERVER's readback, adopted: after a save the dialog shows what
          // the service derived from the document this project now stores,
          // rather than an echo of what was chosen.
          setEditVersion(saved.draftVersion);
          setSupported([...saved.languages.supported]);
          setFallback(saved.languages.default ?? null);
          setConfirmReset(false);
        },
        onError(error) {
          // A stale refusal ends this edit: the selection is kept so it can be
          // carried forward, and saving stays refused, because the only thing a
          // second press could do is write it over a change this dialog never
          // showed.
          if (classifyFlowWriteFailure(error).kind === "stale") {
            setStaleBlocked(true);
            setConfirmReset(false);
          }
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !mutation.isPending) {
          onClose();
        }
      }}
    >
      <DialogContent
        busy={mutation.isPending}
        title="Conversation language"
        description="Which languages this project's sessions run in, and which one they open in."
        footer={
          <>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              Close
            </Button>
            {canWrite && authored ? (
              <Button
                variant="outline"
                onClick={() => setConfirmReset(true)}
                disabled={mutation.isPending || staleBlocked}
              >
                Restore inherited
              </Button>
            ) : null}
            {canWrite ? (
              <Button
                onClick={() => submit(writes)}
                disabled={
                  mutation.isPending ||
                  staleBlocked ||
                  writes.length === 0 ||
                  empty ||
                  defaultUnsupported
                }
              >
                {mutation.isPending ? "Saving…" : "Save"}
              </Button>
            ) : null}
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            A project may only narrow what the pinned pack supports: language
            support is a property of the engine, and content alone cannot make a
            new language work.
          </p>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-foreground">
              Session languages
            </legend>
            {choices.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                The Editor Service could not read which languages this pack
                supports, so none can be offered.
              </p>
            ) : (
              choices.map((code) => (
                <label
                  key={code}
                  className="flex items-center gap-2 text-sm text-foreground"
                >
                  <input
                    type="checkbox"
                    checked={supported.includes(code)}
                    disabled={!canWrite || mutation.isPending}
                    onChange={(event) =>
                      setSupported((current) =>
                        event.target.checked
                          ? // The BASE's order, not click order: the document
                            // this writes is read in order, and a list that
                            // reordered itself by how it was clicked would
                            // change the document without changing the choice.
                            choices.filter(
                              (entry) =>
                                current.includes(entry) || entry === code,
                            )
                          : current.filter((entry) => entry !== code),
                      )
                    }
                  />
                  <span>
                    {languageName(code)}{" "}
                    <code className="text-xs text-muted-foreground">
                      {code}
                    </code>
                  </span>
                </label>
              ))
            )}
          </fieldset>

          <div className="space-y-2">
            <label
              htmlFor="conversation-default-language"
              className="text-sm font-medium text-foreground"
            >
              Sessions open in
            </label>
            <select
              id="conversation-default-language"
              className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground"
              value={fallback ?? ""}
              disabled={!canWrite || mutation.isPending}
              onChange={(event) =>
                setFallback(
                  event.target.value === "" ? null : event.target.value,
                )
              }
            >
              {/*
                NO EMPTY OPTION WHERE ABSENCE CANNOT BE WRITTEN. `default_language`
                is not on the pinned engine's delete allowlist and `set` takes a
                code, so the only op that can leave the key absent is `reset` —
                and a reset clears it only when the BASE supplies none. Round 1
                offered the choice unconditionally and emitted nothing for it, so
                selecting it either disabled Save (reading as "already the case")
                or let the OTHER edits through while the old default silently
                stood. Where it is reachable it is offered and writes the reset;
                where it is not, it is absent and the line below says why.
              */}
              {clearable ? <option value="">No default language</option> : null}
              {choices.map((code) => (
                <option key={code} value={code}>
                  {languageName(code)} ({code})
                </option>
              ))}
            </select>
            {!clearable ? (
              <p className="text-xs text-muted-foreground">
                The base pack always opens a session in a language, so this
                project can choose which one but not none.
              </p>
            ) : null}
          </div>

          {empty ? (
            <Notice tone="warning" role="alert">
              <span>
                At least one session language is required. The pinned engine
                will not load a project that supports none.
              </span>
            </Notice>
          ) : null}

          {defaultUnsupported ? (
            <Notice tone="warning" role="alert">
              <span>
                Sessions cannot open in a language this project does not
                support. Select {languageName(fallback ?? "")} above, or choose a
                different opening language.
              </span>
            </Notice>
          ) : null}

          {failure !== null ? (
            <Notice tone="destructive" role="alert">
              <span>{failureText(failure)}</span>
            </Notice>
          ) : null}

          <p className="text-xs text-muted-foreground">
            Narrowing the session languages does not change what a Rules asset
            must carry: the shared library publishes each rules identity with
            every supported language, and its other-language content may keep
            inheriting the base pack&apos;s unmodified text. Report and preview
            presentation are decided by the target that renders them, not here.
          </p>

          {confirmReset && canWrite && !staleBlocked ? (
            <div className="space-y-2 rounded-md border border-border px-4 py-3">
              <p className="text-sm text-foreground">
                Drop this project&apos;s own languages and use the base
                pack&apos;s again?
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmReset(false)}
                  disabled={mutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  // NAMED APART from the control that opened this panel: two
                  // buttons reading "Restore inherited" in one dialog are
                  // ambiguous in a screen-reader control listing, and the
                  // visible text stays a prefix of the accessible name, which
                  // is what WCAG 2.5.3 requires.
                  aria-label="Restore inherited conversation languages"
                  onClick={() =>
                    submit([
                      { setting: "supportedLanguages", op: "reset" },
                      { setting: "defaultLanguage", op: "reset" },
                    ])
                  }
                  // THE SAME GATE AS EVERY OTHER WRITE HERE. This panel is
                  // opened from a state that permits a write and can outlive
                  // it: `canWrite` folds in whether the read is still current,
                  // so a refetch failing under an open confirmation left a live
                  // write button behind a stopped one.
                  disabled={mutation.isPending || !canWrite || staleBlocked}
                >
                  Restore inherited
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function failureText(
  failure: ReturnType<typeof classifyFlowWriteFailure>,
): string {
  switch (failure.kind) {
    case "stale":
      return "This project's draft changed since this dialog read it, so nothing was written. Your selection is kept, and saving stays refused: applying it now would write it over a change this dialog never showed. Close and reopen this control to see the current languages, then re-enter it.";
    case "conflict":
      return `${failure.message} Nothing was written and your selection is kept.`;
    case "upstream":
      return "The Editor Service could not reach the pinned clinical engine, so it could not check this against the loader. Nothing was written. This one is worth trying again.";
    case "denied":
      return "This account may not change this project's conversation languages. Nothing was written.";
    case "invalid":
      return `${failure.message} Nothing was written and your selection is kept.`;
    case "ambiguous":
      return "The Editor Service did not answer this write, so it is not known whether it was applied. Nothing has been resent. Reload the section before trying again.";
    case "error":
      return `The Editor Service refused this write (${failure.code}). Nothing was written and your selection is kept.`;
  }
}
